import { createAppStore } from '@app/core/stores';
import { ListViewSort } from '../types';

/**
 * Stored state for a single ListView instance.
 * Contains filters, sorting, and pagination state.
 */
export interface StoredListViewState {
  filters: Record<string, any>;
  sort: ListViewSort | null;
  pageIndex: number;
  pageSize: number;
}

/**
 * State structure in the store, keyed by listId.
 */
interface ListViewStateStore {
  states: Record<string, StoredListViewState>;

  /**
   * Get stored state for a specific listId.
   * Returns null if no state exists for the listId.
   */
  getStoredState: (listId: string) => StoredListViewState | null;

  /**
   * Save state for a specific listId.
   * Overwrites any existing state for that listId.
   */
  setStoredState: (listId: string, state: StoredListViewState) => void;

  /**
   * Clear stored state for a specific listId.
   */
  clearStoredState: (listId: string) => void;

  /**
   * Clear all stored states (useful for debugging or resetting all lists).
   */
  clearAllStoredStates: () => void;
}

/**
 * Zustand store for managing sticky ListView state across sessions.
 * Uses persist middleware to save state to localStorage.
 * State is keyed by listId to support multiple ListView instances.
 */
export const useListViewStateStore = createAppStore<ListViewStateStore>('ListViewStateStore', {
  persist: { name: 'app.listView.state', version: 1 },
})((set, get) => ({
  states: {},

  getStoredState: (listId: string) => {
    return get().states[listId] || null;
  },

  setStoredState: (listId: string, state: StoredListViewState) => {
    set((current) => ({
      states: {
        ...current.states,
        [listId]: state,
      },
    }));
  },

  clearStoredState: (listId: string) => {
    set((current) => {
      const { [listId]: _, ...rest } = current.states;
      return { states: rest };
    });
  },

  clearAllStoredStates: () => {
    set({ states: {} });
  },
}));
